import {
  type InsertCoachingPackage,
  PackageType,
  insertCoachingPackageSchema,
  patchCoachingPackageSchema,
} from "@/api/db/schema/coaching-package";
import { coachingPackageService } from "@/api/services/coaching-package.service";
import { queryOptions } from "@tanstack/react-query";
import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { authMiddlewareServer } from "../middleware/auth";

// PUBLIC ENDPOINTS (for coachs.tsx page - no authentication required)

export const listPublicPackagesFn = createServerFn({ method: "GET" }).handler(
  async () => {
    const packages = await coachingPackageService.listPackages();

    // Group by type - flatten data blob for frontend
    const coaching = packages
      .filter((p) => p.packageType === PackageType.COACHING)
      .map((p) => {
        const data = p.data || {};
        return {
          title: data.title || "",
          description: data.description || "",
          price: data.price || 0,
          oldPrice: data.oldPrice,
          priceDetails: data.priceDetails || "",
          features: data.features || [],
          continuationPlan: data.continuationPlan,
          ctaText: data.ctaText || "Satın Al",
          ctaLink: data.ctaLink || "#",
          isRecommended: data.isRecommended || false,
          imageUrl: data.imageUrl,
          packageId: p.packageId,
          packageType: p.packageType,
          isActive: p.isActive,
          displayOrder: p.displayOrder,
        };
      });

    const consulting = packages
      .filter((p) => p.packageType === PackageType.CONSULTING)
      .map((p) => {
        const data = p.data || {};
        return {
          title: data.title || "",
          description: data.description || "",
          price: data.price || 0,
          oldPrice: data.oldPrice,
          priceDetails: data.priceDetails || "",
          features: data.features || [],
          continuationPlan: data.continuationPlan,
          ctaText: data.ctaText || "Satın Al",
          ctaLink: data.ctaLink || "#",
          isRecommended: data.isRecommended || false,
          imageUrl: data.imageUrl,
          packageId: p.packageId,
          packageType: p.packageType,
          isActive: p.isActive,
          displayOrder: p.displayOrder,
        };
      });

    return {
      status: 200,
      body: {
        coaching,
        consulting,
        total: packages.length,
      },
    };
  },
);

export const listPublicPackagesOptions = () =>
  queryOptions({
    queryKey: ["listPublicPackages"],
    queryFn: () => listPublicPackagesFn(),
    staleTime: 1000 * 60 * 5, // Cache for 5 minutes
  });

// ADMIN ENDPOINTS (require admin authentication)

export const listAllPackagesFn = createServerFn({ method: "GET" })
  .middleware([authMiddlewareServer])
  .handler(async ({ context }) => {
    if (context.auth.role !== "admin") {
      return {
        status: 403,
        body: { message: "Forbidden" },
      };
    }

    const packages = await coachingPackageService.listAllPackages();

    return {
      status: 200,
      body: {
        packages,
        total: packages.length,
      },
    };
  });

export const listAllPackagesOptions = () =>
  queryOptions({
    queryKey: ["listAllPackages"],
    queryFn: () => listAllPackagesFn(),
  });

const getPackageByIdSchema = z.object({
  packageId: z.string(),
});

export const getPackageByIdFn = createServerFn({ method: "GET" })
  .middleware([authMiddlewareServer])
  .inputValidator(getPackageByIdSchema)
  .handler(async ({ context, data }) => {
    if (context.auth.role !== "admin") {
      return {
        status: 403,
        body: { message: "Forbidden" },
      };
    }

    const pkg = await coachingPackageService.getPackageById(data.packageId);

    if (!pkg) {
      return {
        status: 404,
        body: { message: "Package not found" },
      };
    }

    return {
      status: 200,
      body: pkg,
    };
  });

export const createPackageFn = createServerFn({ method: "POST" })
  .middleware([authMiddlewareServer])
  .inputValidator(insertCoachingPackageSchema)
  .handler(async ({ context, data }) => {
    if (context.auth.role !== "admin") {
      return {
        status: 403,
        body: { message: "Forbidden" },
      };
    }

    const pkg = await coachingPackageService.createPackage(
      data as InsertCoachingPackage,
    );

    return {
      status: 201,
      body: {
        message: "Package created successfully",
        package: pkg,
      },
    };
  });

export const updatePackageFn = createServerFn({ method: "POST" })
  .middleware([authMiddlewareServer])
  .inputValidator(patchCoachingPackageSchema)
  .handler(async ({ context, data }) => {
    if (context.auth.role !== "admin") {
      return {
        status: 403,
        body: { message: "Forbidden" },
      };
    }

    const { packageId, ...updateData } = data;
    const pkg = await coachingPackageService.updatePackage(
      packageId,
      updateData,
    );

    if (!pkg) {
      return {
        status: 404,
        body: { message: "Package not found" },
      };
    }

    return {
      status: 200,
      body: {
        message: "Package updated successfully",
        package: pkg,
      },
    };
  });

const deletePackageSchema = z.object({
  packageId: z.string(),
});

export const deletePackageFn = createServerFn({ method: "POST" })
  .middleware([authMiddlewareServer])
  .inputValidator(deletePackageSchema)
  .handler(async ({ context, data }) => {
    if (context.auth.role !== "admin") {
      return {
        status: 403,
        body: { message: "Forbidden" },
      };
    }

    const pkg = await coachingPackageService.deletePackage(data.packageId);

    if (!pkg) {
      return {
        status: 404,
        body: { message: "Package not found" },
      };
    }

    return {
      status: 200,
      body: {
        message: "Package deleted successfully",
      },
    };
  });

const toggleSchema = z.object({
  packageId: z.string(),
});

export const toggleActiveFn = createServerFn({ method: "POST" })
  .middleware([authMiddlewareServer])
  .inputValidator(toggleSchema)
  .handler(async ({ context, data }) => {
    if (context.auth.role !== "admin") {
      return {
        status: 403,
        body: { message: "Forbidden" },
      };
    }

    const pkg = await coachingPackageService.toggleActive(data.packageId);

    if (!pkg) {
      return {
        status: 404,
        body: { message: "Package not found" },
      };
    }

    return {
      status: 200,
      body: {
        message: `Package ${pkg.isActive ? "activated" : "deactivated"}`,
        package: pkg,
      },
    };
  });

export const toggleRecommendedFn = createServerFn({ method: "POST" })
  .middleware([authMiddlewareServer])
  .inputValidator(toggleSchema)
  .handler(async ({ context, data }) => {
    if (context.auth.role !== "admin") {
      return {
        status: 403,
        body: { message: "Forbidden" },
      };
    }

    const pkg = await coachingPackageService.toggleRecommended(data.packageId);

    if (!pkg) {
      return {
        status: 404,
        body: { message: "Package not found" },
      };
    }

    return {
      status: 200,
      body: {
        message: `Package ${pkg.data.isRecommended ? "marked as recommended" : "unmarked as recommended"}`,
        package: pkg,
      },
    };
  });
